import { useAppSelector } from '../../../store/redux/hooks';
import { Box, Circle } from '@chakra-ui/react';
import { useEffect, useRef, useMemo, useCallback } from 'react';

interface CanvasEyeDropperDimensions {
  width: number;
  height: number;
}

interface ImageMap {
  logo: string | undefined;
  icon: string | undefined;
  banner: string | undefined;
  thumbnail?: string | undefined;
}

// Constants
const LOGO_ASPECT_RATIO = 110 / 360;
const ICON_SIZE = 200;
const THUMBNAIL_SIZE = 200;
const MAX_CANVAS_WIDTH = 403;
const CANVAS_WIDTH_PADDING = 100;
const CANVAS_WIDTH_FALLBACK = 93;
const PREVIEW_ELEMENT_SIZE = 55;
const PREVIEW_OFFSET_X = 15;
const PREVIEW_OFFSET_Y = 15;
const PREVIEW_BOUNDARY_OFFSET = 10;
const CANVAS_BORDER_RADIUS = '9px';
const LOGO_BACKGROUND_COLOR = '#EFEFEF';

// Banner aspect ratios by template type
const BANNER_ASPECT_RATIOS = {
  eventTicket: 294 / 1125,
  loyalty: 432 / 1125,
  default: 370 / 1125,
} as const;

interface CanvasEyeDropperProps {
  color: string;
  onChange: (color: string) => void;
  dropperImage: string;
}

const CanvasEyeDropper = ({
  color,
  onChange,
  dropperImage,
}: CanvasEyeDropperProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const previewElemRef = useRef<HTMLDivElement>(null);
  const uuid = useAppSelector((s) => s.template.uuid);
  const templateType = useAppSelector((s) => s.template.templateType);
  const images = useAppSelector((s) => s.template.images);
  const { shared, apple, google } = images;

  // Helper function to get image based on type and platform
  const getImageByType = useCallback(
    (
      type: 'logo' | 'icon' | 'banner' | 'thumbnail',
      platform: 'shared' | 'apple' | 'google'
    ): string | undefined => {
      const imageMap: Record<string, ImageMap> = {
        shared: {
          logo: shared.logo,
          icon: shared.icon,
          banner: shared.banner,
        },
        apple: {
          logo: apple?.logo,
          icon: apple?.icon,
          banner: apple?.banner,
          thumbnail: apple?.thumbnail,
        },
        google: {
          logo: google?.logo,
          icon: google?.icon,
          banner: google?.banner,
        },
      };
      return imageMap[platform]?.[type];
    },
    [shared, apple, google]
  );

  // Calculate canvas dimensions based on image type
  const dimensions = useMemo((): CanvasEyeDropperDimensions | null => {
    if (!dropperImage) return null;

    const calculateResponsiveWidth = (): number => {
      const availableWidth = window.innerWidth - CANVAS_WIDTH_PADDING;
      return availableWidth > MAX_CANVAS_WIDTH
        ? MAX_CANVAS_WIDTH
        : window.innerWidth - CANVAS_WIDTH_FALLBACK;
    };

    if (dropperImage.includes('logo')) {
      const width = calculateResponsiveWidth();
      return {
        width,
        height: width * LOGO_ASPECT_RATIO,
      };
    }

    if (dropperImage.includes('icon')) {
      return {
        width: ICON_SIZE,
        height: ICON_SIZE,
      };
    }

    if (dropperImage.includes('banner')) {
      const aspectRatio =
        BANNER_ASPECT_RATIOS[
          templateType as keyof typeof BANNER_ASPECT_RATIOS
        ] || BANNER_ASPECT_RATIOS.default;
      const width = calculateResponsiveWidth();
      return {
        width,
        height: width * aspectRatio,
      };
    }

    if (dropperImage.includes('thumbnail')) {
      return {
        width: THUMBNAIL_SIZE,
        height: THUMBNAIL_SIZE,
      };
    }

    return null;
  }, [dropperImage, templateType]);

  // Get base image URL
  const baseImage = useMemo((): string | undefined => {
    if (!dropperImage) return undefined;

    const [platform, type] = dropperImage.split('-') as [string, string];
    if (!platform || !type) return undefined;

    const normalizedPlatform = platform as 'shared' | 'apple' | 'google';
    const normalizedType = type as 'logo' | 'icon' | 'banner' | 'thumbnail';

    return getImageByType(normalizedType, normalizedPlatform);
  }, [dropperImage, getImageByType]);

  // Build image URL with cache busting
  const getImageUrl = useCallback(
    (imageUrl: string | undefined): string | undefined => {
      if (!imageUrl) return undefined;

      if (imageUrl.startsWith('data:')) {
        return imageUrl;
      }
      return uuid ? `${imageUrl}?v=${Date.now()}` : imageUrl;
    },
    [uuid]
  );

  // Convert RGB to hex
  const rgbToHex = useCallback((r: number, g: number, b: number): string => {
    return (
      '#' +
      [r, g, b]
        .map((x) => {
          const hex = x.toString(16);
          return hex.length === 1 ? '0' + hex : hex;
        })
        .join('')
    );
  }, []);

  // Pick color from canvas at mouse position
  const colorPickUp = useCallback(
    (
      context: CanvasRenderingContext2D,
      xMousePosition: number,
      yMousePosition: number
    ): string => {
      const pixel = context.getImageData(xMousePosition, yMousePosition, 1, 1);
      const data = pixel.data;

      const r = data[0];
      const g = data[1]; // Fixed: was incorrectly assigned to b
      const b = data[2]; // Fixed: was incorrectly assigned to g

      return rgbToHex(r, g, b);
    },
    [rgbToHex]
  );

  // Set preview element position
  const setPreviewElementPosition = useCallback(
    (xMousePosition: number, yMousePosition: number) => {
      const previewElem = previewElemRef.current;
      if (!previewElem || !dimensions) return;

      const widthOffset = dimensions.width - PREVIEW_ELEMENT_SIZE;
      const heightOffset = dimensions.height - PREVIEW_ELEMENT_SIZE;

      previewElem.style.left =
        xMousePosition > widthOffset
          ? `${widthOffset - PREVIEW_BOUNDARY_OFFSET}px`
          : `${xMousePosition + PREVIEW_OFFSET_X}px`;
      previewElem.style.top =
        yMousePosition > heightOffset
          ? `${heightOffset - PREVIEW_BOUNDARY_OFFSET}px`
          : `${yMousePosition + PREVIEW_OFFSET_Y}px`;
    },
    [dimensions]
  );

  // Load and draw image on canvas
  const loadAndDrawImage = useCallback(
    (
      ctx: CanvasRenderingContext2D,
      imageUrl: string | undefined,
      width: number,
      height: number,
      fillBackground = false
    ) => {
      if (!imageUrl) return;

      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.src = getImageUrl(imageUrl) || imageUrl;

      image.onload = () => {
        if (fillBackground) {
          // Logo images have transparent background
          ctx.fillStyle = LOGO_BACKGROUND_COLOR;
          ctx.fillRect(0, 0, width, height);
        }
        ctx.drawImage(image, 0, 0, width, height);
      };
    },
    [getImageUrl]
  );

  // Handle mouse hover for color preview
  const handleHoverColor = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas || !dimensions) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const x = e.nativeEvent.offsetX;
      const y = e.nativeEvent.offsetY;

      setPreviewElementPosition(x, y);

      const hexColor = colorPickUp(ctx, x, y);
      if (previewElemRef.current) {
        previewElemRef.current.style.backgroundColor = hexColor;
      }
    },
    [dimensions, setPreviewElementPosition, colorPickUp]
  );

  // Handle color selection on click
  const applySelectedColorHandler = useCallback(
    (e: React.MouseEvent<HTMLCanvasElement>) => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const ctx = canvas.getContext('2d');
      if (!ctx) return;

      const x = e.nativeEvent.offsetX;
      const y = e.nativeEvent.offsetY;

      const hexColor = colorPickUp(ctx, x, y);
      onChange(hexColor);
    },
    [colorPickUp, onChange]
  );

  // Initialize canvas and load image
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !dimensions || !baseImage) return;

    canvas.style.borderRadius = CANVAS_BORDER_RADIUS;
    canvas.width = dimensions.width;
    canvas.height = dimensions.height;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const isLogo = dropperImage?.includes('logo');
    loadAndDrawImage(
      ctx,
      baseImage,
      dimensions.width,
      dimensions.height,
      isLogo
    );
  }, [baseImage, dimensions, dropperImage, loadAndDrawImage]);

  return (
    <Box position="relative">
      <canvas
        ref={canvasRef}
        onMouseMove={handleHoverColor}
        onClick={applySelectedColorHandler}
      />
      <Circle
        ref={previewElemRef}
        size="40px"
        border="1px"
        borderColor="gray.400"
        bg={color}
        position="absolute"
        left="0px"
        top="0px"
      ></Circle>
    </Box>
  );
};

export default CanvasEyeDropper;
